import React, { useState, useEffect, useRef } from "react";
import Modal from "@/components/Modal";
import Dropdown from "@/components/elements/Dropdown";
import Label from "@/components/elements/Label";
import NumberInput from "@/components/elements/NumberInput";
import api from "@/services/api";
import { uploadFile, triggerFileUpload, clearFileInput } from "@/services/fileUpload";

/**
 * CutOutInputList component that contains a button to add cutouts and manage their properties
 *
 * @returns {JSX.Element} The rendered CutOutInputList component
 */
const CutOutInputList = ({
    shapes,
    addShape,
    plateDimensions = { width: 100, height: 200 },
    updateShape,
    selectedShapeId,
    onSelectShape,
    productInfo,
    onDeleteShape,
    uploadDrawingUrl,
}) => {

    // Hard-coded dropdown options (can be replaced later with dynamic data)
    const CUT_OUT_TYPE = [
        { value: 0, name: "-- Selecteer type --" },
        { value: "GLASS", name: "Glas" },
        { value: "MAILBOX", name: "Brievenbus" },
        { value: "PEEPHOLE", name: "Spionoog" },
        { value: "NONE", name: "Vrije sparing" },
	      // commented because this will end up getting rebuild into a separate option
        // { value: "DRAWING", name: "Tekening" },
    ];

    // Options for an empty cut-out (leeg)
    const EMPTY_CUT_OUT_SHAPES = [
        { value: 0, name: "-- Selecteer vorm --" },
        { value: "CIRCLE", name: "Rond" },
        { value: "RECTANGLE", name: "Rechthoekig" },
    ];

    const drawingref = useRef(null);

    // Store added shapes with their properties
    const [addedShapes, setAddedShapes] = useState(() => {
        // Ensure all shapes have the required centering properties with default values
        return shapes.map(shape => ({
            ...shape,
            is_horizontally_centered: shape.is_horizontally_centered ?? false,
            is_vertically_centered: shape.is_vertically_centered ?? false,
        }));
    });

    // State for centering flags for the selected shape
    const [centerHorizontal, setCenterHorizontal] = useState(false);
    const [centerVertical, setCenterVertical] = useState(false);
    const [referenceHorizontal, setReferenceHorizontal] = useState("left"); // 'left' or 'right'
    const [referenceVertical, setReferenceVertical] = useState("top"); // 'top' or 'bottom'
    const [horizontalBoth, setHorizontalBoth] = useState(false); // when true use both edges
    const [verticalBoth, setVerticalBoth] = useState(false); // when true use both edges

    // Store latest updateShape callback in ref to avoid dependency issues
    const updateShapeRef = useRef(updateShape);
    updateShapeRef.current = updateShape;
    const drawingRef = useRef(null);

    // State for dynamically fetched articles
    const [cutOutArticles, setCutOutArticles] = useState([
        { value: 0, name: "-- Selecteer artikel --" }
    ]);
    const [articlesLoading, setArticlesLoading] = useState(false);

	// State for using the generated drawing url
	const [drawingUrl, setDrawingUrl] = useState(null);

    // State for controlling the delete confirmation modal
    const [modalOpen, setModalOpen] = useState(false);

    // Local state for the dropdown selections inside the modal
    const [cutOutProperties, setCutOutProperties] = useState({
        type: 0,
        shape: 0,
        article: 0,
        width: 0,
        height: 0,
        radius: 0,
        x: 0,
        y: 0,
    });

    // API function to get cut-out articles
    const getCutOutArticles = (category) => api.get(`/api/products/cut-out/${category}`)

    // Fetch articles when cut-out type changes
    useEffect(() => {

        const fetchArticles = async () => {

            // Only fetch for types that have articles (GLASS, MAILBOX, PEEPHOLE)
            if (!cutOutProperties.type || !["GLASS", "MAILBOX", "PEEPHOLE"].includes(cutOutProperties.type)) {
                setCutOutArticles([{ value: 0, name: "-- Selecteer artikel --" }]);
                return;
            }

            setArticlesLoading(true);

            try {
                const response = await getCutOutArticles(cutOutProperties.type);
                const articles = response.data.products.map(product => ({
                    value: product.value,
                    name: product.name,
                    shape: product.shape,
                    width: product.width,
                    height: product.height,
                    radius: product.radius,
                    is_horizontally_centered: false,
                    is_vertically_centered: false,
                }));

                setCutOutArticles([
                    { value: 0, name: "-- Selecteer artikel --" },
                    ...articles
                ]);
            } catch (error) {
                console.error('Error fetching cut-out articles:', error);
                setCutOutArticles([
                    { value: 0, name: "-- Selecteer artikel --" },
                    { value: 'error', name: 'Fout bij laden artikelen' }
                ]);
            } finally {
                setArticlesLoading(false);
            }
        };

        fetchArticles();
    }, [cutOutProperties.type]);

    /**
     * Generate alphabetical identifier (A, B, C, ..., Z)
     * @param {number} index - Index starting from 0
     * @returns {string} Alphabetical identifier
     */
    const generateIdentifier = (index) => {
        return String.fromCharCode(65 + index); // 65 is ASCII for 'A'
    };

    const regenerateAllIdentifiers = () => {
        setAddedShapes((prev) => prev.map((shape, index) => ({ ...shape, identifier: generateIdentifier(index) })));
    }

    /**
     * Handle button click - for now this does nothing
     */
    const handleModalState = () => {
        setModalOpen(prevState => !prevState);
    }

    const handleDeleteShape = (shapeId) => {
        onDeleteShape(shapeId);
        setAddedShapes((prev) => prev.filter(shape => shape.identifier !== shapeId));
        regenerateAllIdentifiers();
    }

    /**
     * Handle Excel file upload
     * Uploads file with current thickness and TL position data
     * Replaces current variations with uploaded data
     *
     * @param {FileList} files - Selected files from file input
     */
    const handleDrawingUpload = (files) => {
        uploadFile(uploadDrawingUrl, files[0]).then((response) => {
			setDrawingUrl(response.data.fileName);
            clearFileInput(drawingref);
        });
    };

    /**
     * Callback passed to the Dropdown component to keep track of the selected values.
     * The Dropdown component supplies an updated properties object.
     */
    const updateCutOutProperties = (props /*, property */) => {
        setCutOutProperties(props);
    };

    const addNewCutOut = () => {

        if (typeof addShape === "function") {
            const identifier = generateIdentifier(addedShapes.length);

            // Handle empty cutouts (NONE type)
            if (
                cutOutProperties.type === "NONE" &&
                (cutOutProperties.shape === "RECTANGLE" || cutOutProperties.shape === "CIRCLE")
            ) {

                const is_horizontally_centered = cutOutProperties.is_horizontally_centered ?? false;
                const is_vertically_centered = cutOutProperties.is_vertically_centered ?? false;

                if (cutOutProperties.shape === "RECTANGLE") {
                    // Calculate actual values for the rectangle
                    const actualWidth = cutOutProperties.width > 0 ? cutOutProperties.width : 20;
                    const actualHeight = cutOutProperties.height > 0 ? cutOutProperties.height : 20;
                    const actualX = cutOutProperties.x > 0 ? cutOutProperties.x : (plateDimensions.width - actualWidth) / 2;
                    const actualY = cutOutProperties.y > 0 ? cutOutProperties.y : (plateDimensions.height - actualHeight) / 2;


                    const shapeData = {
                        identifier: identifier,
                        type: "NONE",
                        shape: "RECTANGLE",
                        width: actualWidth,
                        height: actualHeight,
                        x: actualX,
                        y: actualY,
                        is_horizontally_centered: is_horizontally_centered,
                        is_vertically_centered: is_vertically_centered,
                    };

                    addShape(shapeData);
                    setAddedShapes((prev) => [...prev, shapeData]);
                } else if (cutOutProperties.shape === "CIRCLE") {
                    // Calculate actual values for the circle
                    const actualRadius = cutOutProperties.radius > 0 ? cutOutProperties.radius : 10;
                    const actualX = cutOutProperties.x > 0 ? cutOutProperties.x : plateDimensions.width / 2;
                    const actualY = cutOutProperties.y > 0 ? cutOutProperties.y : plateDimensions.height / 2;

                    const shapeData = {
                        identifier: identifier,
                        type: "NONE",
                        shape: "CIRCLE",
                        radius: actualRadius,
                        x: actualX,
                        y: actualY,
                        is_horizontally_centered: is_horizontally_centered,
                        is_vertically_centered: is_vertically_centered,
                    };

                    addShape(shapeData);
                    setAddedShapes((prev) => [...prev, shapeData]);
                }
            }
            // Handle articles (GLASS, MAILBOX, PEEPHOLE types)
            else if (
                ["GLASS", "MAILBOX", "PEEPHOLE"].includes(cutOutProperties.type) &&
                cutOutProperties.article > 0
            ) {

                const is_horizontally_centered = cutOutProperties.is_horizontally_centered ?? false;
                const is_vertically_centered = cutOutProperties.is_vertically_centered ?? false;

                // Find the selected article from the cutOutArticles array
                const selectedArticle = cutOutArticles.find(article => article.value === cutOutProperties.article);

                if (selectedArticle) {
                    // Calculate position - use provided coordinates or center the shape
                    const actualX = cutOutProperties.x > 0 ? cutOutProperties.x :
                        selectedArticle.shape === "CIRCLE"
                            ? plateDimensions.width / 2
                            : (plateDimensions.width - selectedArticle.width) / 2;
                    const actualY = cutOutProperties.y > 0 ? cutOutProperties.y :
                        selectedArticle.shape === "CIRCLE"
                            ? plateDimensions.height / 2
                            : (plateDimensions.height - selectedArticle.height) / 2;

                    const shapeData = {
                        identifier: identifier,
                        type: cutOutProperties.type,
                        shape: selectedArticle.shape,
                        product: selectedArticle.value, // Add product ID for validation
                        articleName: selectedArticle.name,
                        x: actualX,
                        y: actualY,
                        is_horizontally_centered: is_horizontally_centered,
                        is_vertically_centered: is_vertically_centered,
                    };

                    // Add shape-specific properties with predefined dimensions
                    if (selectedArticle.shape === "RECTANGLE") {
                        shapeData.width = selectedArticle.width;
                        shapeData.height = selectedArticle.height;
                    } else if (selectedArticle.shape === "CIRCLE") {
                        shapeData.radius = selectedArticle.radius;
                    }

                    addShape(shapeData);
                    setAddedShapes((prev) => [...prev, shapeData]);
                }
            }
			else if (cutOutProperties.type === 'DRAWING') {
				const shapeData = {
					identifier: identifier,
					type: "DRAWING",
					fileName: drawingUrl
				};

				addShape(shapeData);
				setAddedShapes((prev) => [...prev, shapeData]);
			}
        }

        // Close modal and reset form
        setModalOpen(false);
        setCutOutProperties({ type: 0, shape: 0, article: 0, width: 0, height: 0, radius: 0, x: 0, y: 0 });
    }

    // Update shape properties - can handle single property or multiple properties
    const updateShapeProperties = (shapeId, propertyOrUpdates, value = null) => {
        let updates;

        // Handle both single property update and multiple property updates
        if (typeof propertyOrUpdates === 'string') {
            updates = { [propertyOrUpdates]: value };
        } else {
            updates = propertyOrUpdates;
        }

        setAddedShapes((prev) =>
            prev.map((shape) => {
                if (shape.identifier === shapeId) {
                    // For articles (shapes with product ID), only allow updating position (x, y) and centered options.
                    if (shape.product) {
                        const allowedProperties = ["x", "y", "is_horizontally_centered", "is_vertically_centered"];
                        const invalidProps = Object.keys(updates).filter(prop => !allowedProperties.includes(prop));
                        if (invalidProps.length > 0) {
                            console.warn(`Cannot update ${invalidProps.join(', ')} for article - dimensions are fixed`);
                            // Filter out invalid properties
                            const validUpdates = Object.keys(updates)
                                .filter(prop => allowedProperties.includes(prop))
                                .reduce((obj, key) => {
                                    obj[key] = updates[key];
                                    return obj;
                                }, {});
                            return { ...shape, ...validUpdates };
                        }
                    }
                    return { ...shape, ...updates };
                }

                return shape;
            })
        );

        // Update canvas shape as well
        if (typeof updateShape === "function") {
            // Find the updated shape
            const updatedShape = addedShapes.find(shape => shape.identifier === shapeId);
            if (updatedShape) {
                // For articles (shapes with product ID), only allow updating position
                if (updatedShape.product) {
                    const allowedProperties = ["x", "y", "is_horizontally_centered", "is_vertically_centered"];
                    const invalidProps = Object.keys(updates).filter(prop => !allowedProperties.includes(prop));
                    if (invalidProps.length > 0) {
                        return;
                    }
                }

                let shapeData = {
                    identifier: updatedShape.identifier,
                    type: updatedShape.type,
                    shape: updatedShape.shape,
                    is_horizontally_centered: updates.hasOwnProperty('is_horizontally_centered') ? updates.is_horizontally_centered : updatedShape.is_horizontally_centered,
                    is_vertically_centered: updates.hasOwnProperty('is_vertically_centered') ? updates.is_vertically_centered : updatedShape.is_vertically_centered,
                    x: updates.hasOwnProperty('x') ? updates.x : updatedShape.x,
                    y: updates.hasOwnProperty('y') ? updates.y : updatedShape.y,
                };

                // Add product ID if this is an article
                if (updatedShape.product) {
                    shapeData.product = updatedShape.product;
                    shapeData.articleName = updatedShape.articleName;
                }

                if (updatedShape.shape === "RECTANGLE") {
                    shapeData.width = updates.hasOwnProperty('width') ? updates.width : updatedShape.width;
                    shapeData.height = updates.hasOwnProperty('height') ? updates.height : updatedShape.height;
                } else if (updatedShape.shape === "CIRCLE") {
                    shapeData.radius = updates.hasOwnProperty('radius') ? updates.radius : updatedShape.radius;
                }

                updateShape(shapeData);
            }
        }
    };

    /**
     * Handle property updates for the selected shape
     * @param {string} property - The property being updated
     * @param {*} value - The new value
     */
    const handlePropertyUpdate = (property) => (updatedProps) => {
        const selectedShape = addedShapes.find(shape => shape.identifier === selectedShapeId);

        if (selectedShape) {
            const updates = { [property]: updatedProps[property] };

            // If width is updated and shape is horizontally centered, recalculate x position
            if (property === 'width' && selectedShape.is_horizontally_centered) {
                const newWidth = updatedProps[property];
                updates.x = (plateDimensions.width - newWidth) / 2;
            }

            // If height is updated and shape is vertically centered, recalculate y position
            if (property === 'height' && selectedShape.is_vertically_centered) {
                const newHeight = updatedProps[property];
                updates.y = (plateDimensions.height - newHeight) / 2;
            }

            // If radius is updated and shape is centered, recalculate position(s)
            if (property === 'radius') {
                if (selectedShape.is_horizontally_centered) {
                    updates.x = plateDimensions.width / 2;
                }
                if (selectedShape.is_vertically_centered) {
                    updates.y = plateDimensions.height / 2;
                }
            }

            updateShapeProperties(selectedShape.identifier, updates);
        }
    };

    // /**
    //  * Calculate and update centered horizontal position
    //  */
    // const updateCenteredHorizontal = useCallback((shapeToCenter = null) => {
    //     const selectedShape = shapeToCenter || addedShapes.find(shape => shape.identifier === selectedShapeId);
    //     if (centerHorizontal && selectedShape) {
    //         let centeredX;
    //         if (selectedShape.shape === "RECTANGLE") {
    //             centeredX = (plateDimensions.width - (selectedShape.width || 20)) / 2;
    //         } else if (selectedShape.shape === "CIRCLE") {
    //             centeredX = plateDimensions.width / 2;
    //         }
    //         updateShapeProperties(selectedShape.identifier, "x", centeredX);
    //     }
    // }, [centerHorizontal, selectedShapeId, plateDimensions.width]);

    // /**
    //  * Calculate and update centered vertical position
    //  */
    // const updateCenteredVertical = useCallback((shapeToCenter = null) => {
    //     const selectedShape = shapeToCenter || addedShapes.find(shape => shape.identifier === selectedShapeId);
    //     if (centerVertical && selectedShape) {
    //         let centeredY;
    //         if (selectedShape.shape === "RECTANGLE") {
    //             centeredY = (plateDimensions.height - (selectedShape.height || 20)) / 2;
    //         } else if (selectedShape.shape === "CIRCLE") {
    //             centeredY = plateDimensions.height / 2;
    //         }
    //         updateShapeProperties(selectedShape.identifier, "y", centeredY);
    //     }
    // }, [centerVertical, selectedShapeId, plateDimensions.height]);

    /**
     * Handle centering checkbox change
     * @param {'horizontal'|'vertical'} direction - The direction to center
     * @param {Event} event - The checkbox change event
     */
    const handleCenterChange = (direction, event) => {
        const isChecked = event.target.checked;
        const isHorizontal = direction === 'horizontal';

        // Update the appropriate state
        if (isHorizontal) {
            setCenterHorizontal(isChecked);
        } else {
            setCenterVertical(isChecked);
        }

        const selectedShape = addedShapes.find(shape => shape.identifier === selectedShapeId);

        if (selectedShape) {
            const centeringProp = isHorizontal ? 'is_horizontally_centered' : 'is_vertically_centered';
            const updates = {
                [centeringProp]: isChecked
            };

            if (isChecked) {
                // Calculate centered position immediately
                const plateDimension = isHorizontal ? plateDimensions.width : plateDimensions.height;
                const shapeDimension = isHorizontal ? (selectedShape.width || 20) : (selectedShape.height || 20);
                const positionProp = isHorizontal ? 'x' : 'y';

                let centeredPosition;
                if (selectedShape.shape === "RECTANGLE") {
                    centeredPosition = (plateDimension - shapeDimension) / 2;
                } else if (selectedShape.shape === "CIRCLE") {
                    centeredPosition = plateDimension / 2;
                }
                updates[positionProp] = centeredPosition;
            }

            // Update both properties in a single call
            updateShapeProperties(selectedShape.identifier, updates);
        }
    };

    /**
     * Convert shape position to display value based on reference edge
     * @param {'horizontal'|'vertical'} direction - The direction to calculate for
     */
    const getPositionDisplayValue = (direction) => {
        const selectedShape = addedShapes.find(shape => shape.identifier === selectedShapeId);
        if (!selectedShape) return 0;

        const isHorizontal = direction === 'horizontal';
        const bothMode = isHorizontal ? horizontalBoth : verticalBoth;
        const reference = isHorizontal ? referenceHorizontal : referenceVertical;
        const plateDimension = isHorizontal ? plateDimensions.width : plateDimensions.height;
        const position = isHorizontal ? (selectedShape.x || 0) : (selectedShape.y || 0);
        const size = isHorizontal ? (selectedShape.width || 0) : (selectedShape.height || 0);
        const radius = selectedShape.radius || 0;
        const primaryEdge = isHorizontal ? 'left' : 'top';

        if (bothMode) {
            if (selectedShape.shape === "CIRCLE") {
                // For circles, show margin (distance from each side) while keeping the circle centered
                return Math.max(0, (plateDimension / 2) - radius);
            }
            return position;
        }

        if (reference === primaryEdge) {
            if (selectedShape.shape === "CIRCLE") {
                // For circles from primary edge, show distance from edge to circle edge
                return Math.max(0, position - radius);
            }
            return position;
        }

        // reference from secondary edge (right/bottom)
        if (selectedShape.shape === "RECTANGLE") {
            return Math.max(0, plateDimension - position - size);
        }
        // For circles from secondary edge, show distance from edge to circle edge
        return Math.max(0, plateDimension - position - radius);
    };

    /**
     * Handle manual position input change
     * @param {'horizontal'|'vertical'} direction - The direction to handle
     * @param {Event} e - The input change event
     */
    const handlePositionInputChange = (direction, e) => {
        const value = parseInt(e.target.value) || 0;
        const selectedShape = addedShapes.find(shape => shape.identifier === selectedShapeId);
        if (!selectedShape) return;

        const isHorizontal = direction === 'horizontal';
        const bothMode = isHorizontal ? horizontalBoth : verticalBoth;
        const reference = isHorizontal ? referenceHorizontal : referenceVertical;
        const plateDimension = isHorizontal ? plateDimensions.width : plateDimensions.height;
        const positionProp = isHorizontal ? 'x' : 'y';
        const sizeProp = isHorizontal ? 'width' : 'height';
        const primaryEdge = isHorizontal ? 'left' : 'top';

        let newPos;
        if (bothMode) {
            if (selectedShape.shape === "RECTANGLE") {
                newPos = value;
                updateShapeProperties(selectedShape.identifier, positionProp, newPos);
                const newSize = Math.max(1, plateDimension - 2 * newPos);
                updateShapeProperties(selectedShape.identifier, sizeProp, newSize);
            } else if (selectedShape.shape === "CIRCLE") {
                const margin = value;
                const newRadius = Math.max(1, (plateDimension - 2 * margin) / 2);
                updateShapeProperties(selectedShape.identifier, "radius", newRadius);
                // Ensure the circle stays centered
                updateShapeProperties(selectedShape.identifier, positionProp, plateDimension / 2);
            }
        } else {
            if (reference === primaryEdge) {
                if (selectedShape.shape === "CIRCLE") {
                    // For circles from primary edge, position center so edge is at desired distance
                    newPos = value + (selectedShape.radius || 0);
                } else {
                    newPos = value;
                }
            } else {
                // from secondary edge (right/bottom)
                if (selectedShape.shape === "RECTANGLE") {
                    const size = isHorizontal ? (selectedShape.width || 0) : (selectedShape.height || 0);
                    newPos = plateDimension - value - size;
                } else {
                    // For circles from secondary edge, position center so edge is at desired distance
                    newPos = plateDimension - value - (selectedShape.radius || 0);
                }
            }
            updateShapeProperties(selectedShape.identifier, positionProp, newPos);
        }
    };

    // Show article dropdown only for specific cut-out types
    const showArticleDropdown = ["GLASS", "MAILBOX", "PEEPHOLE"].includes(
        cutOutProperties.type,
    );

    // Show shape dropdown when cut-out type is 'NONE' (leeg)
    const showEmptyShapeDropdown = cutOutProperties.type === "NONE";

    const showDrawingUpload = cutOutProperties.type === "DRAWING";

    /**
     * Get product name by product ID
     * @param {number} productId - The product ID to look up
     * @returns {string} The product name or empty string if not found
     */
    const getProductNameById = (productId) => {
        if (!productInfo || !productId) return '';
        const product = productInfo.find(p => p.value === productId);
        return product ? product.name : '';
    };

    // Find the selected shape
    const selectedShape = selectedShapeId ? addedShapes.find(shape => shape.identifier === selectedShapeId) : null;

    return (
        <div className="cutout-input-list">

            {/* Shape List */}
            {addedShapes.length > 0 && (
                <div className="mb-4">
                    <h6 className="mb-3">Sparingen</h6>
                    <div className="d-flex flex-wrap">
                        {addedShapes.map((shape) => (
                            <button
                                key={shape.identifier}
                                type="button"
                                className={`mr-1 mb-1 btn btn-sm ${shape.identifier === selectedShapeId ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => onSelectShape && onSelectShape(shape.identifier)}
                            >
                                {shape.product ? (
                                    getProductNameById(shape.product) + " " + shape.identifier
                                ) : (
                                    "Sparing " + shape.identifier
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="cutout-input-list__items">
                {selectedShape ? (
                    <div className="cutout-input-item mb-3">
                        <h6 className="mb-3">
                            {selectedShape.product ? (
                                <small className="text-muted ml-2">{getProductNameById(selectedShape.product)}</small>
                            ) : (
                                <small className="text-muted ml-2">Sparing {selectedShape.identifier}</small>
                            )}

                        </h6>

                        <div className="row">
                            {selectedShape.type === 'DRAWING' ? (
                                <div className="col-md-12">
                                    <img
                                        src={`/storage/cut-outs/${selectedShape.fileName}`}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </div>
                            ) : (
                                <>
                                    <div className="col-md-6">
                                        {selectedShape.shape === "RECTANGLE" ? (
                                            <>
                                                <Label label="Breedte" small />
                                                <NumberInput
                                                    properties={selectedShape}
                                                    property="width"
                                                    field={{
                                                        min: 1,
                                                        max: 1000,
                                                        unit: "mm",
                                                        disabled: horizontalBoth || selectedShape.product, // Check for product ID
                                                        readonly: selectedShape.product // Check for product ID
                                                    }}
                                                    updateProperties={handlePropertyUpdate("width")}
                                                />
                                                <Label label="Hoogte" small />
                                                <NumberInput
                                                    properties={selectedShape}
                                                    property="height"
                                                    field={{
                                                        min: 1,
                                                        max: 1000,
                                                        unit: "mm",
                                                        disabled: verticalBoth || selectedShape.product, // Check for product ID
                                                        readonly: selectedShape.product // Check for product ID
                                                    }}
                                                    updateProperties={handlePropertyUpdate("height")}
                                                />
                                            </>
                                        ) : selectedShape.shape === "CIRCLE" ? (
                                            <>
                                                <Label label="Radius" small />
                                                <NumberInput
                                                    properties={selectedShape}
                                                    property="radius"
                                                    field={{
                                                        min: 1,
                                                        max: 500,
                                                        unit: "mm",
                                                        disabled: selectedShape.product, // Check for product ID
                                                        readonly: selectedShape.product // Check for product ID
                                                    }}
                                                    updateProperties={handlePropertyUpdate("radius")}
                                                />
                                            </>
                                        ) : null}
                                    </div>
                                    <div className="col-md-6">
                                        <Label
                                            label={horizontalBoth ? "Positie links EN rechts" : (referenceHorizontal === "left" ? "Positie vanaf links" : "Positie vanaf rechts")}
                                            small
                                        />
                                        <div className="d-flex align-items-center mb-2">
                                            <input
                                                type="number"
                                                className="form-control mr-2"
                                                value={getPositionDisplayValue('horizontal')}
                                                onChange={(e) => handlePositionInputChange('horizontal', e)}
                                                disabled={selectedShape.is_horizontally_centered || false}
                                                min={0}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-secondary mr-2"
                                                onClick={() =>
                                                    setReferenceHorizontal((prev) =>
                                                        prev === "left" ? "right" : "left",
                                                    )
                                                }
                                                disabled={horizontalBoth}
                                            >
                                                {referenceHorizontal === "left" ? "←" : "→"}
                                            </button>
                                        </div>

                                        {/* Center horizontally */}
                                        <input
                                            id={`center-horizontal-${selectedShape.identifier}`}
                                            name={`center-horizontal-${selectedShape.identifier}`}
                                            className="mr-1"
                                            type="checkbox"
                                            checked={selectedShape.is_horizontally_centered || false}
                                            onChange={(e) => handleCenterChange('horizontal', e)}
                                        />
                                        <Label
                                            label="Centreer"
                                            small
                                            labelfor={`center-horizontal-${selectedShape.identifier}`}
                                        />

                                        <Label
                                            label={verticalBoth ? "Positie boven EN onder" : (referenceVertical === "top" ? "Positie vanaf boven" : "Positie vanaf onder")}
                                            small
                                        />
                                        <div className="d-flex align-items-center mb-2">
                                            <input
                                                type="number"
                                                className="form-control mr-2"
                                                value={getPositionDisplayValue('vertical')}
                                                onChange={(e) => handlePositionInputChange('vertical', e)}
                                                disabled={selectedShape.is_vertically_centered || false}
                                                min={0}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-secondary mr-2"
                                                onClick={() =>
                                                    setReferenceVertical((prev) =>
                                                        prev === "top" ? "bottom" : "top",
                                                    )
                                                }
                                                disabled={verticalBoth}
                                            >
                                                {referenceVertical === "top" ? "↑" : "↓"}
                                            </button>
                                        </div>

                                        {/* Center vertically */}
                                        <input
                                            id={`center-vertical-${selectedShape.identifier}`}
                                            name={`center-vertical-${selectedShape.identifier}`}
                                            className="mr-1"
                                            type="checkbox"
                                            checked={selectedShape.is_vertically_centered || false}
                                            onChange={(e) => handleCenterChange('vertical', e)}
                                        />
                                        <Label
                                            label="Centreer"
                                            small
                                            labelfor={`center-vertical-${selectedShape.identifier}`}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                ) : addedShapes.length > 0 ? (
                    <div className="alert alert-info">
                        <p>Selecteer een sparing hierboven of in de tekening om de instellingen aan te passen.</p>
                    </div>
                ) : (
                    <div className="alert alert-secondary">
                        <p>Voeg een sparing toe om te beginnen.</p>
                    </div>
                )}
            </div>

            <div className="d-flex justify-content-between">
                {/* Add button */}
                <a
                    type="button"
                    className="navigationbutton cutout-input-list__add-button m-0 mr-2"
                    onClick={() => handleModalState(true)}
                >
                    <svg className="cutout-input-list__button-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                        {/* !Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc. */}
                        <path d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32v144H48c-17.7 0-32 14.3-32 32s14.3 32 32 32h144v144c0 17.7 14.3 32 32 32s32-14.3 32-32V288h144c17.7 0 32-14.3 32-32s-14.3-32-32-32H256V80z"/>
                    </svg>

                    Sparing toevoegen
                </a>

                {/* Delete button */}
                {selectedShape && (
                    <a className="btn btn-danger cutout-input-list__delete-button m-0" onClick={() => handleDeleteShape(selectedShape.identifier)}>
                        <svg
                            className="cutout-input-list__button-icon"
                        fill="currentColor"
                        height="18"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fillRule="evenodd"
                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                            clipRule="evenodd"
                        />
                    </svg>
                    Sparing verwijderen
                </a>
                )}
            </div>
            <Modal
                open={modalOpen}
                title={"Sparing toevoegen"}
                content={
                    "Voeg een sparing toe aan de plaat"
                }
                savetitle="Toevoegen"
                canceltitle="Annuleer"
                buttonclass="btn-orange"
                datacontent={
                    <div className="cutout-form">
                        <div className="d-block">
                            <Label label="Type" small />
                            <Dropdown
                                options={CUT_OUT_TYPE}
                                properties={cutOutProperties}
                                property="type"
                                field={{}}
                                updateProperties={updateCutOutProperties}
                            />
                        </div>
                        {showArticleDropdown && (
                            <div className="d-block">
                                <Label label="Artikel" small />
                                <Dropdown
                                    options={cutOutArticles}
                                    properties={cutOutProperties}
                                    property="article"
                                    field={{}}
                                    updateProperties={updateCutOutProperties}
                                />
                            </div>
                        )}

                        {showEmptyShapeDropdown && (
                            <div className="d-block">
                                <Label label="Vorm" small />
                                <Dropdown
                                    options={EMPTY_CUT_OUT_SHAPES}
                                    properties={cutOutProperties}
                                    property="shape"
                                    field={{}}
                                    updateProperties={updateCutOutProperties}
                                />
                            </div>
                        )}

                        {showDrawingUpload && (
                            <>

                                <div className="d-block mt-3">
                                    <a
                                        name="Upload tekening"
                                        href="#"
                                        className="btn-orange mt-0"
                                        onClick={e => triggerFileUpload(e, drawingref)}
                                    >
                                        Upload tekening
                                    </a>

                                    {drawingUrl && (
                                        <img
                                            src={`/storage/cut-outs/${drawingUrl}`}
                                            style={{
                                                width: '100%',
                                                maxWidth: 200,
                                                maxHeight: 200,
                                                float: 'right'
                                            }}
                                        />
                                    )}
                                </div>

                                {/* Hidden file input for Excel upload */}
                                <input
                                type="file"
                                onChange={(e) => handleDrawingUpload(e.target.files)}
                                className="d-none"
                                ref={drawingref} />
                            </>
                        )}

                    </div>
                }
                savefunction={addNewCutOut}
                updateOpen={() => handleModalState(false)}
            />
        </div>
    );
};

export default CutOutInputList;
